package dev.lingvo.ops.state

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import dev.lingvo.ops.components.OpsLoad
import dev.lingvo.shared.ops.OpsPage
import dev.lingvo.site.ds.LPageState

/** Keeps real cursor history; changing the filter creates a new window at the newest page. */
class OpsResourcePaging<T>(pageSize: Int, deepLink: String? = null) {
    private val window = KeysetWindow<T>(pageSize).apply { deepLink?.let(::seedFromDeepLink) }
    private var pending = false
    private var acceptedCursor: String? = deepLink
    private var canGoOlder = false
    var cursor by mutableStateOf(deepLink)
        private set

    fun state(page: OpsPage<T>): LPageState {
        window.accept(page)
        pending = false
        acceptedCursor = cursor
        canGoOlder = page.nextCursor != null
        return window.state
    }

    /** Settle navigation only from the current request's real result, never a previous page count. */
    fun <R> stateFor(resource: OpsResourceState<R>, pageOf: (R) -> OpsPage<T>): LPageState = when (val load = resource.load) {
        is OpsLoad.Ready -> state(pageOf(load.value)).let { result ->
            canGoOlder = canGoOlder && !resource.isRefreshing && resource.refreshFailure == null
            result.copy(hasOlder = canGoOlder)
        }
        is OpsLoad.Failed -> {
            pending = false
            canGoOlder = false
            window.state.copy(shown = 0, pageSize = 0, hasOlder = false)
        }
        OpsLoad.Loading -> {
            pending = true
            canGoOlder = false
            window.state.copy(shown = 0, pageSize = 0, hasOlder = false)
        }
        is OpsLoad.OfflineStale -> {
            pending = false
            canGoOlder = false
            window.state.copy(shown = 0, pageSize = 0, hasOlder = false)
        }
    }

    fun older(page: OpsPage<T>) {
        if (pending || !canGoOlder || acceptedCursor != cursor) return
        window.accept(page)
        window.older()?.let { pending = true; canGoOlder = false; cursor = it }
    }

    fun newer() {
        if (pending || !window.state.hasNewer) return
        pending = true
        canGoOlder = false
        cursor = window.newer()
    }
}

@Composable
fun <T> rememberOpsPaging(filter: Any?, pageSize: Int = 100, deepLink: String? = null): OpsResourcePaging<T> =
    remember(filter) { OpsResourcePaging(pageSize, deepLink) }
